import { t } from '@lingui/core/macro';
import { styled, Tooltip } from '@mui/material';
import { XIcon } from 'lucide-react';
import React, { type JSX } from 'react';

import { colorSystem } from '../../../theme';
import { ChipIconButton } from '../../Chip';
import { Divider } from '../../Divider';
import { Text } from '../../Text';
import { Actions, Argument, ParameterOption, SearchBlock } from '../types';
import { formatArgument } from '../util/formatArgument';
import { formatOperator } from '../util/formatOperator';
import { TEXT_SEARCH_FIELD_ID } from './constants';

export function SearchBlockView<T, TAutocompleteState>({
    block,
    dispatch = () => {},
    autocompleteState,
    parameter,
}: {
    block: SearchBlock<T>;
    autocompleteState?: TAutocompleteState;
    dispatch: React.Dispatch<Actions<T, TAutocompleteState>>;
    parameter?: ParameterOption<T, TAutocompleteState, Argument>;
}): JSX.Element {
    const operatorLabel = parameter?.ops.find((op) => op.op === block.op)?.label;

    if (block.field === TEXT_SEARCH_FIELD_ID) {
        return (
            <FilterContainer>
                <SectionParameter
                    onClick={() => {
                        dispatch({ type: 'removeSearchBlock', searchBlock: block });
                        dispatch({ type: 'onQueryChange', query: String(block.parameter) });
                    }}
                >
                    <Text variant="body-small">{block.parameter}</Text>
                </SectionParameter>

                <Divider orientation="vertical" />

                <ChipIconButton
                    aria-label={t`Remove filter: ${String(block.parameter)}`}
                    onClick={() => {
                        dispatch({ type: 'removeSearchBlock', searchBlock: block });
                    }}
                >
                    <XIcon />
                </ChipIconButton>
            </FilterContainer>
        );
    }

    return (
        <FilterContainer>
            <SectionField>
                <Text variant="body-small">{parameter?.label ?? parameter?.field ?? block.field}</Text>
            </SectionField>

            <Divider orientation="vertical" />

            <SectionOperator
                onClick={() => {
                    dispatch({ type: 'editOperator', searchBlock: block });
                }}
            >
                <Text color={colorSystem.neutral[7]} variant="body-small" style={{ whiteSpace: 'nowrap' }}>
                    {operatorLabel || formatOperator(block.op)}
                </Text>
            </SectionOperator>

            <Divider orientation="vertical" />

            <SectionParameter
                onClick={() => {
                    dispatch({ type: 'editParameter', searchBlock: block });
                }}
            >
                <Tooltip
                    title={
                        Array.isArray(block.parameter)
                            ? formatArgument(parameter, block.parameter, autocompleteState)
                            : ''
                    }
                >
                    <Text
                        variant="body-small"
                        style={{ overflow: 'hidden', whiteSpace: 'nowrap', textOverflow: 'ellipsis' }}
                    >
                        {formatArgument(parameter, block.parameter, autocompleteState)}
                    </Text>
                </Tooltip>
            </SectionParameter>

            <Divider orientation="vertical" />

            <ChipIconButton
                aria-label={t`Remove filter: ${parameter?.label ?? parameter?.field ?? block.field}`}
                onClick={() => {
                    dispatch({ type: 'removeSearchBlock', searchBlock: block });
                }}
            >
                <XIcon />
            </ChipIconButton>
        </FilterContainer>
    );
}

const FilterContainer = styled('span')({
    boxSizing: 'border-box',
    display: 'inline-flex',
    alignItems: 'center',
    background: `linear-gradient(${colorSystem.neutral[1]},${colorSystem.neutral[1]})`,
    border: `1px solid ${colorSystem.neutral[2]}`,
    borderRadius: 4,
    height: 24,
});

const FilterSection = styled('span')({
    padding: '4px 8px',
    display: 'inline-flex',
    alignItems: 'center',
    transition: 'background 0.2s linear',
    '&:hover': {
        background: colorSystem.neutral[2],
    },
    overflow: 'hidden',
    cursor: 'pointer',
});

const SectionField = styled('span')({
    padding: '4px 8px',
    display: 'inline-flex',
    alignItems: 'center',
    overflow: 'hidden',
});

const SectionOperator = styled(FilterSection)({
    color: colorSystem.neutral[7],
});

const SectionParameter = styled(FilterSection)({});
